<script setup lang="ts">
import { computed, onMounted, watch, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import AdminLayout from '@/components/layout/AdminLayout.vue'
import { useUsers } from '@/composables/useUsers'
import type { UserStatusFilter } from '@/types/users'

const { t } = useI18n()
const router = useRouter()
const { items, meta, loading, error, search, status, sort, page, fetchList } = useUsers()

// Debounce search
let searchDebounce: ReturnType<typeof setTimeout> | undefined
const searchInput = ref('')

watch(searchInput, (val) => {
  clearTimeout(searchDebounce)
  searchDebounce = setTimeout(() => {
    search.value = val
    page.value = 1
    void fetchList()
  }, 350)
})

function onStatusChange(event: Event): void {
  const target = event.target as HTMLSelectElement
  const val = target.value
  status.value = val === '' ? undefined : (val as UserStatusFilter)
  page.value = 1
  void fetchList()
}

const lastLoginAriaSort = computed<'ascending' | 'descending' | 'none'>(() => {
  if (sort.value === 'last_login_desc') return 'descending'
  if (sort.value === 'last_login_asc') return 'ascending'
  return 'none'
})

function toggleLastLoginSort(): void {
  sort.value = sort.value === 'last_login_desc' ? 'last_login_asc' : 'last_login_desc'
  page.value = 1
  void fetchList()
}

function goToDetail(id: number): void {
  void router.push({ name: 'admin.users.show', params: { id } })
}

function prevPage(): void {
  if (page.value > 1) {
    page.value--
    void fetchList()
  }
}

function nextPage(): void {
  if (meta.value && page.value < meta.value.last_page) {
    page.value++
    void fetchList()
  }
}

onMounted(fetchList)
</script>

<template>
  <AdminLayout>
    <div class="space-y-6">
      <div class="flex items-center justify-between">
        <h1 class="text-2xl font-bold text-white">{{ t('admin.users.title') }}</h1>
      </div>

      <!-- Search + Filter row -->
      <div class="flex flex-col sm:flex-row gap-3">
        <input
          v-model="searchInput"
          type="search"
          :placeholder="t('admin.users.search_placeholder')"
          class="w-full sm:max-w-xs rounded-lg bg-slate-800 border border-slate-600 px-3 py-2 text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
        />
        <select
          :value="status ?? ''"
          class="rounded-lg bg-slate-800 border border-slate-600 px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
          @change="onStatusChange"
        >
          <option value="">{{ t('admin.users.status_all') }}</option>
          <option value="active">{{ t('admin.users.status_active') }}</option>
          <option value="deactivated">{{ t('admin.users.status_deactivated') }}</option>
          <option value="unverified">{{ t('admin.users.status_unverified') }}</option>
        </select>
      </div>

      <!-- Loading state -->
      <div v-if="loading" class="space-y-2">
        <div
          v-for="i in 5"
          :key="i"
          class="h-12 bg-slate-800 rounded-lg animate-pulse"
        />
      </div>

      <!-- Error state -->
      <div
        v-else-if="error"
        class="bg-red-500/10 border border-red-500/30 rounded-2xl p-6 flex items-center justify-between"
      >
        <p class="text-red-300 text-sm">{{ t('admin.common.error_loading') }}</p>
        <button
          type="button"
          class="text-sm text-red-300 hover:text-red-200 font-medium underline"
          @click="fetchList"
        >
          {{ t('admin.common.retry') }}
        </button>
      </div>

      <!-- Empty state -->
      <div
        v-else-if="items.length === 0"
        class="bg-slate-800 border border-slate-700 rounded-2xl p-12 text-center"
      >
        <p class="text-slate-400">{{ t('admin.users.no_results') }}</p>
      </div>

      <!-- Table -->
      <div v-else class="bg-slate-800 border border-slate-700 rounded-2xl overflow-hidden">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b border-slate-700">
              <th class="text-left px-4 py-3 text-slate-400 font-medium">{{ t('admin.users.name') }}</th>
              <th class="text-left px-4 py-3 text-slate-400 font-medium">{{ t('admin.users.email') }}</th>
              <th class="text-left px-4 py-3 text-slate-400 font-medium">{{ t('admin.users.verified') }}</th>
              <th class="text-left px-4 py-3 text-slate-400 font-medium">{{ t('admin.users.deactivated') }}</th>
              <th class="text-left px-4 py-3 text-slate-400 font-medium">{{ t('admin.users.registrations') }}</th>
              <th class="text-left px-4 py-3 text-slate-400 font-medium">{{ t('admin.users.created_at') }}</th>
              <th class="text-left px-4 py-3 text-slate-400 font-medium" :aria-sort="lastLoginAriaSort">
                <button
                  type="button"
                  class="inline-flex items-center gap-1 font-medium hover:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 rounded"
                  data-testid="sort-last-login"
                  @click="toggleLastLoginSort"
                >
                  {{ t('admin.users.last_login_at') }}
                  <span v-if="sort === 'last_login_desc'" aria-hidden="true">↓</span>
                  <span v-else-if="sort === 'last_login_asc'" aria-hidden="true">↑</span>
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="user in items"
              :key="user.id"
              class="border-b border-slate-700 last:border-0 hover:bg-slate-700/50 cursor-pointer transition-colors"
              @click="goToDetail(user.id)"
            >
              <td class="px-4 py-3 text-white font-medium">{{ user.name }}</td>
              <td class="px-4 py-3 text-slate-300">{{ user.email }}</td>
              <td class="px-4 py-3">
                <span
                  class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                  :class="user.email_verified
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : 'bg-amber-500/20 text-amber-400'"
                >
                  {{ user.email_verified ? t('admin.users.badge_verified') : t('admin.users.badge_unverified') }}
                </span>
              </td>
              <td class="px-4 py-3">
                <span
                  v-if="user.deactivated"
                  class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-red-500/20 text-red-400"
                >
                  {{ t('admin.users.badge_deactivated') }}
                </span>
                <span v-else class="text-slate-500 text-xs">—</span>
              </td>
              <td class="px-4 py-3 text-slate-300">{{ user.registrations_count }}</td>
              <td class="px-4 py-3 text-slate-400 text-xs">
                {{ new Date(user.created_at).toLocaleDateString() }}
              </td>
              <td
                class="px-4 py-3 text-xs"
                :class="user.last_login_at ? 'text-slate-400' : 'text-slate-500'"
                data-testid="last-login-cell"
              >
                {{ user.last_login_at ? new Date(user.last_login_at).toLocaleString() : t('admin.users.never_logged_in') }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="meta && meta.last_page > 1" class="flex items-center justify-between text-sm text-slate-400">
        <span>{{ t('admin.common.page_of', { current: meta.current_page, total: meta.last_page }) }}</span>
        <div class="flex gap-2">
          <button
            type="button"
            :disabled="page <= 1"
            class="px-3 py-1.5 rounded-lg border border-slate-600 hover:border-slate-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            @click="prevPage"
          >
            {{ t('admin.common.previous') }}
          </button>
          <button
            type="button"
            :disabled="meta.current_page >= meta.last_page"
            class="px-3 py-1.5 rounded-lg border border-slate-600 hover:border-slate-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            @click="nextPage"
          >
            {{ t('admin.common.next') }}
          </button>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>
